import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  MapPin, Calendar, Clock, Home, Users, UtensilsCrossed,
  CheckCircle2, AlertCircle, Loader2, ArrowLeft, BedDouble,
  DollarSign, Backpack, Navigation, Plane, Thermometer, ExternalLink, QrCode,
  Hotel, Phone, Ban, XCircle, Undo2, Shirt
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import TravelBoard from './TravelBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  paid_full: { label: 'Confirmed', color: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  deposit_paid: { label: 'Deposit Received', color: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  pending: { label: 'Pending Payment', color: 'bg-orange-500', badge: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  waitlist: { label: 'Waitlisted', color: 'bg-violet-500', badge: 'bg-violet-50 text-violet-700 border-violet-200', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle },
};

const PACKING_LIST = {
  essentials: [
    'Pillow & sheets are PROVIDED — bring your own only if you prefer',
    'Blanket or sleeping bag if you like extra warmth at night',
    'Bath towel + hand towel + washcloth',
    'Shower toiletries (soap, shampoo, conditioner)',
    'Toothbrush, toothpaste, deodorant',
    'Any prescription or daily medications',
    'Phone + charger',
    'Valid photo ID',
    'Refillable water bottle',
    'Journal / pen',
    'A book or two (downtime is part of the rhythm)',
  ],
  clothing: [
    'Warm jacket or fleece (nights around 50°F, can dip into the 40s)',
    'Hoodie or sweatshirt for evenings around the fire',
    'Layerable long-sleeve shirts',
    'T-shirts',
    'Comfortable pants / jeans',
    'Shorts (days near 70°F)',
    'Rain jacket or windbreaker (September showers are common)',
    'Comfortable walking shoes',
    'Sandals or flip-flops for the cabin / showers',
    'Warm socks + everyday socks',
    'Sleepwear',
    'Hat or beanie',
    'Underwear for 3 days',
  ],
  outdoor: [
    'Sunscreen',
    'Bug spray / insect repellent',
    'Sunglasses',
    'Flashlight or headlamp (cabin paths get dark)',
    'Small daypack for activities',
    'Earplugs (cabins are shared)',
  ],
};

const AttendeePortal = () => {
  const { attendeeId } = useParams();
  const [data, setData] = useState(null);
  const [ticketToken, setTicketToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Cancel-attendance flow
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [settlementChoice, setSettlementChoice] = useState('refund'); // 'refund' | 'donate'
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [rsvpSaving, setRsvpSaving] = useState(false);

  const handleDinnerRsvp = async (choice) => {
    setRsvpSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/portal/${attendeeId}/dinner-rsvp`, { rsvp: choice });
      setData(d => ({ ...d, attendee: { ...d.attendee, thursday_dinner_rsvp: choice } }));
      toast.success(choice === 'yes' ? "You're in for Thursday night! See you at the cookout." : 'RSVP recorded — sorry you can\'t make Thursday.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your RSVP — please try again.');
    } finally {
      setRsvpSaving(false);
    }
  };
  const [undoSubmitting, setUndoSubmitting] = useState(false);
  const [savingTshirt, setSavingTshirt] = useState(false);

  const handleTshirtChange = async (newSize) => {
    const size = newSize === 'none' ? '' : newSize;
    setSavingTshirt(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/portal/${attendeeId}/tshirt-size`, { tshirt_size: size });
      setData(prev => prev ? { ...prev, attendee: { ...prev.attendee, tshirt_size: size || null } } : prev);
      toast.success(size ? `T-shirt size updated to ${size}` : 'T-shirt size cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update size — please try again.');
    } finally {
      setSavingTshirt(false);
    }
  };

  const fetchPortal = useCallback(async () => {
    try {
      const [portalRes, tokenRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/portal/${attendeeId}`),
        axios.get(`${BACKEND_URL}/api/retreat/tickets/${attendeeId}/token`).catch(() => ({ data: { token: null } })),
      ]);
      setData(portalRes.data);
      setTicketToken(tokenRes.data?.token || null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Attendee not found' : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [attendeeId]);

  useEffect(() => {
    fetchPortal();
  }, [fetchPortal]);

  const handleSubmitCancel = async () => {
    setCancelSubmitting(true);
    try {
      const paid = Number(data?.payment?.amount_paid || 0);
      const res = await axios.post(`${BACKEND_URL}/api/retreat/portal/${attendeeId}/cancel-attendance`, {
        reason: cancelReason.trim() || null,
        settlement_choice: paid > 0 ? settlementChoice : null,
      });
      if (res.data?.donation_recorded) {
        toast.success(`Thank you — your $${Number(res.data.amount).toFixed(0)} gift to ROJO is recorded. You can undo this within 24 hours.`);
      } else if (res.data?.refund_recorded) {
        toast.success(`Refund of $${Number(res.data.amount).toFixed(0)} marked owed. The team will reach out to settle. You can undo this within 24 hours.`);
      } else {
        toast.success("We've let the team know. You can undo this within 24 hours.");
      }
      setShowCancelModal(false);
      setCancelReason('');
      setSettlementChoice('refund');
      await fetchPortal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record cancellation. Please try again.');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleUndoCancel = async () => {
    setUndoSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/portal/${attendeeId}/uncancel-attendance`);
      toast.success("You're back in. Welcome, brother.");
      await fetchPortal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not undo cancellation. Email info@rojoretreat.com.');
    } finally {
      setUndoSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="portal-loading">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
          <p className="text-sm text-gray-500 tracking-wide">Loading your retreat details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="portal-error">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-900">{error}</p>
            <p className="text-sm text-gray-500 mt-2">Check your link or contact the retreat organizers.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { attendee, room, roommates, payment, retreat } = data;
  const status = STATUS_MAP[attendee.ticket_status] || STATUS_MAP.pending;
  const StatusIcon = status.icon;
  const qrValue = ticketToken || `ROJO-TICKET:${attendee.id}`;
  const isCheckedIn = !!attendee.checked_in_at;
  const isCancelled = attendee.ticket_status === 'cancelled';
  // June 30th confirm-by deadline — banner hides itself once it passes
  const beforeCancelDeadline = Date.now() < new Date('2026-07-01T00:00:00-05:00').getTime();

  // 24-hour undo window for self-service uncancel
  let canUndoCancel = false;
  let hoursLeftToUndo = 0;
  if (isCancelled && attendee.cancelled_at) {
    const cancelledAt = new Date(attendee.cancelled_at);
    const ageHours = (Date.now() - cancelledAt.getTime()) / 3600000;
    canUndoCancel = ageHours <= 24;
    hoursLeftToUndo = Math.max(0, Math.ceil(24 - ageHours));
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="attendee-portal">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200/80 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm" data-testid="portal-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-xl font-semibold tracking-tight bg-gradient-to-br from-red-500 to-orange-500 bg-clip-text text-transparent">ROJO</span>
            <span className="text-[10px] eyebrow text-gray-400 hidden sm:inline">Retreat 2026</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
        {/* Thursday Night Cookout RSVP */}
        {!isCancelled && attendee.ticket_status !== 'waitlist' && (
          <Card className="overflow-hidden border-2 border-amber-300 shadow-sm" data-testid="portal-dinner-rsvp-card">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-500" />
            <CardContent className="p-6 sm:p-8">
              <p className="eyebrow text-amber-600 mb-1">The night before — RSVP needed</p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">
                Thursday Night Cookout
              </h2>
              <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                We&apos;re getting together the night before the retreat for a casual cookout — good food,
                hanging out, and kicking off the weekend together.
              </p>
              <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-gray-800">
                <p className="font-semibold">Shawn Greseth&apos;s House</p>
                <p>20 CR-20, Maple Plain, MN 55359</p>
              </div>
              <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                <span className="font-semibold">If you&apos;re local:</span> please bring two chairs — one for
                yourself and one to share with someone traveling in from out of town.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleDinnerRsvp('yes')}
                  disabled={rsvpSaving}
                  data-testid="dinner-rsvp-yes-btn"
                  className={`flex-1 ${attendee.thursday_dinner_rsvp === 'yes'
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white ring-2 ring-emerald-300'
                    : 'bg-white border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50'}`}
                >
                  {attendee.thursday_dinner_rsvp === 'yes' ? "✓ You're in — see you Thursday!" : "I'll be there"}
                </Button>
                <Button
                  onClick={() => handleDinnerRsvp('no')}
                  disabled={rsvpSaving}
                  data-testid="dinner-rsvp-no-btn"
                  className={`flex-1 ${attendee.thursday_dinner_rsvp === 'no'
                    ? 'bg-gray-700 hover:bg-gray-800 text-white ring-2 ring-gray-300'
                    : 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  {attendee.thursday_dinner_rsvp === 'no' ? "✓ Can't make it" : "Can't make it"}
                </Button>
              </div>
              {!attendee.thursday_dinner_rsvp && (
                <p className="text-xs text-gray-400 mt-3">Please RSVP so we can get an accurate headcount and have enough food for everyone.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ticket Card */}
        <Card className="overflow-hidden border-0 shadow-sm" data-testid="portal-ticket-card">
          <div className={`h-1.5 ${status.color}`} />
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <p className="eyebrow text-gray-400 mb-1">Attendee</p>
                <h1 className="font-display text-4xl sm:text-5xl font-semibold text-gray-900 tracking-tight leading-tight" data-testid="portal-attendee-name">
                  {attendee.first_name} {attendee.last_name}
                </h1>
                <p className="text-sm text-gray-500 mt-1.5">{attendee.email}</p>
              </div>
              <Badge className={`${status.badge} border px-3 py-1.5 text-xs font-semibold self-start`} data-testid="portal-status-badge">
                <StatusIcon className="w-3.5 h-3.5 mr-1.5" />
                {status.label}
              </Badge>
            </div>

            <Separator className="my-6" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InfoRow icon={Calendar} label="Dates" value={retreat.dates} testId="portal-dates" />
              <InfoRow icon={MapPin} label="Location" value={retreat.location} testId="portal-location" />
              <InfoRow icon={Clock} label="Check-in" value={retreat.check_in} testId="portal-checkin" />
              <InfoRow icon={Clock} label="Check-out" value={retreat.check_out} testId="portal-checkout" />
            </div>

            {/* Cancelled banner (with undo within 24h) */}
            {isCancelled && (
              <div
                className="mt-6 rounded-xl border border-red-200 bg-red-50/70 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                data-testid="portal-cancelled-banner"
              >
                <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-900">{"You've cancelled your spot."}</p>
                  {attendee.donation_amount && attendee.donation_status === 'donated' ? (
                    <p className="text-xs text-amber-800 mt-1 font-medium" data-testid="portal-donation-noted">
                      Your ${Number(attendee.donation_amount).toFixed(0)} gift to ROJO is recorded. Thank you, brother.
                    </p>
                  ) : null}
                  {attendee.refund_amount && attendee.refund_status === 'owed' ? (
                    <p className="text-xs text-red-800 mt-1 font-medium" data-testid="portal-refund-noted">
                      Refund of ${Number(attendee.refund_amount).toFixed(0)} is owed to you. The team will reach out to settle.
                    </p>
                  ) : null}
                  {attendee.refund_amount && attendee.refund_status === 'paid' ? (
                    <p className="text-xs text-emerald-800 mt-1 font-medium" data-testid="portal-refund-paid">
                      Your ${Number(attendee.refund_amount).toFixed(0)} refund has been settled.
                    </p>
                  ) : null}
                  {canUndoCancel ? (
                    <p className="text-xs text-red-700 mt-0.5">
                      Changed your mind? You can undo this for the next{' '}
                      <span className="font-semibold">{hoursLeftToUndo} hour{hoursLeftToUndo === 1 ? '' : 's'}</span>.
                      {attendee.donation_amount && attendee.donation_status === 'donated' ? ' Your donation will be reversed if you undo.' : ''}
                      {attendee.refund_amount && attendee.refund_status === 'owed' ? ' The pending refund will also be cancelled.' : ''}
                    </p>
                  ) : (
                    <p className="text-xs text-red-700 mt-0.5">
                      The 24-hour undo window has passed.{' '}
                      <a href="mailto:info@rojoretreat.com" className="underline underline-offset-2 font-medium">Email us</a>{' '}
                      if you want to come back.
                    </p>
                  )}
                </div>
                {canUndoCancel && (
                  <Button
                    onClick={handleUndoCancel}
                    disabled={undoSubmitting}
                    className="bg-white border border-red-200 text-red-700 hover:bg-red-100 hover:text-red-800 shrink-0"
                    data-testid="portal-undo-cancel-btn"
                  >
                    {undoSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Undo2 className="w-4 h-4 mr-1.5" />
                        I changed my mind
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* "I can't make it" CTA — hidden once cancelled or checked in */}
            {!isCancelled && !isCheckedIn && (
              <div className="mt-6 pt-5 border-t border-gray-100" data-testid="portal-cant-make-it-section">
                {beforeCancelDeadline && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:p-5 mb-4">
                  <p className="text-xs font-bold tracking-widest text-amber-700 uppercase mb-2">
                    Action needed by June 30th
                  </p>
                  <p className="text-sm text-amber-900 leading-relaxed">
                    {"If you already know you can't make the September 18–20 retreat, please confirm by "}
                    <span className="font-semibold">June 30th</span>{" so we can finalize headcounts, rooms, and the caravan plan."}
                  </p>
                  <ol className="mt-3 space-y-1.5 text-sm text-amber-900 list-decimal list-inside">
                    <li>Use the {`"I can't make it"`} button below to mark yourself as not attending.</li>
                    <li>
                      <span className="font-semibold">Then email</span>{' '}
                      <a href="mailto:info@rojoretreat.com?subject=Can%27t%20make%20it%20-%20refund%20or%20donate" className="underline underline-offset-2 font-medium hover:text-amber-700">
                        info@rojoretreat.com
                      </a>{' '}
                      to tell us whether you want to <span className="font-semibold">donate your ticket</span> to the retreat fund or <span className="font-semibold">request a refund</span>.
                    </li>
                  </ol>
                  <p className="mt-3 text-xs text-amber-800 italic">
                    {"We need your email reply, not just the portal update, to process refunds/donations correctly."}
                  </p>
                </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Something come up?</p>
                    <p className="text-xs text-gray-500 mt-0.5">{"You can change your mind within 24 hours of cancelling."}</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelModal(true)}
                    className="border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-700 shrink-0"
                    data-testid="portal-cant-make-it-btn"
                  >
                    <Ban className="w-4 h-4 mr-1.5" />
                    {"I can't make it"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* QR Code Check-in Card */}
        <Card className="border-0 shadow-sm overflow-hidden" data-testid="portal-qr-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className={`shrink-0 p-3 rounded-xl ${isCheckedIn ? 'bg-emerald-50' : 'bg-white border-2 border-gray-100'}`}>
                <QRCodeSVG
                  value={qrValue}
                  size={132}
                  level="M"
                  fgColor={isCheckedIn ? '#059669' : '#111827'}
                  includeMargin={false}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <QrCode className="w-4 h-4 text-gray-400" />
                  <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Your Ticket</p>
                </div>
                {isCheckedIn ? (
                  <>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700 mb-2">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Checked In
                    </div>
                    <p className="text-sm text-gray-600">
                      You arrived on{' '}
                      <span className="font-medium text-gray-800">
                        {new Date(attendee.checked_in_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>. Welcome, brother.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-base font-semibold text-gray-900 mb-1">Your secure ticket</p>
                    <p className="text-sm text-gray-600">
                      When you arrive at camp, show this to staff. Only the ROJO check-in scanner can read it — random phone cameras won't reveal your info.
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Breakdown */}
        {payment && (
          <Card className="border-0 shadow-sm" data-testid="portal-payment-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Payment</p>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Base Price</p>
                  <p className="text-lg font-bold text-gray-900">${payment.base_price}</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Paid</p>
                  <p className="text-lg font-bold text-emerald-700">${payment.amount_paid}</p>
                </div>
                <div className={`text-center p-3 rounded-lg ${payment.balance_remaining > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                  <p className="text-xs text-gray-500 mb-1">Remaining</p>
                  <p className={`text-lg font-bold ${payment.balance_remaining > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                    ${payment.balance_remaining}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600">{payment.summary}</p>

              {/* How to pay — only show when there's still a balance owed */}
              {Number(payment.balance_remaining) > 0 && !isCancelled && (
                <div
                  className="mt-5 pt-5 border-t border-gray-100"
                  data-testid="portal-how-to-pay"
                >
                  <p className="text-xs font-bold tracking-widest text-gray-700 uppercase mb-3">
                    How to pay your ${Number(payment.balance_remaining).toFixed(0)} balance
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-4 sm:gap-6 items-center">
                    {/* Venmo QR */}
                    <a
                      href="https://venmo.com/code?user_id=4484655516485443999&created=1765380663.6551008&printed=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mx-auto sm:mx-0 shrink-0 block rounded-xl overflow-hidden border-2 border-gray-200 hover:border-red-400 transition-colors"
                      data-testid="portal-venmo-qr-link"
                    >
                      <img
                        src="/venmo-qr.png"
                        alt="Venmo QR code — scan to pay ROJO Retreat"
                        className="w-32 h-32 sm:w-36 sm:h-36 object-cover"
                      />
                    </a>
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-gray-900">Venmo (recommended)</p>
                      <p className="text-gray-600 leading-relaxed">
                        Scan the QR code with your phone, or{' '}
                        <a
                          href="https://venmo.com/code?user_id=4484655516485443999&created=1765380663.6551008&printed=1"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:text-red-700 underline underline-offset-2 font-medium"
                          data-testid="portal-venmo-link"
                        >
                          tap here to open Venmo
                        </a>{' '}
                        directly. Please include your name in the note so we can match your payment.
                      </p>
                      <p className="text-xs text-gray-500 leading-relaxed pt-1">
                        Prefer to pay by check?{' '}
                        <a
                          href="mailto:info@rojoretreat.com?subject=Check%20payment%20for%20ROJO%20Retreat"
                          className="text-gray-700 underline underline-offset-2"
                          data-testid="portal-check-email"
                        >
                          Email info@rojoretreat.com
                        </a>{' '}
                        and we'll send instructions.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Room & Roommates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm" data-testid="portal-room-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Home className="w-4 h-4 text-gray-400" />
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Room</p>
              </div>
              {data.room_hidden ? (
                <p className="text-sm text-gray-500" data-testid="portal-room-hidden">
                  Room assignments will be revealed closer to the retreat — see you at check-in.
                </p>
              ) : room ? (
                <div>
                  <p className="text-lg font-bold text-gray-900">{room.name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                    <span className="capitalize">{room.room_type}</span>
                    {room.floor && <span>Floor {room.floor}</span>}
                    {room.building && <span>{room.building}</span>}
                  </div>
                  {attendee.bed_assignment && (
                    <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-600">
                      <BedDouble className="w-3.5 h-3.5" />
                      {attendee.bed_assignment}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Not yet assigned</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="portal-roommates-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-gray-400" />
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Roommates</p>
              </div>
              {roommates.length > 0 ? (
                <div className="space-y-2">
                  {roommates.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                        {r.first_name[0]}{r.last_name[0]}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{r.first_name} {r.last_name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  {data.room_hidden ? 'Revealed at check-in' : room ? 'No roommates assigned yet' : 'Assigned after room placement'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* T-Shirt Size — attendee-editable */}
        <Card className="border-0 shadow-sm" data-testid="portal-tshirt-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Shirt className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">T-Shirt Size</p>
                  <p className="text-xs text-gray-500 mt-0.5">The size you pick is the size you&apos;ll receive at the retreat.</p>
                </div>
              </div>
              <Select
                value={attendee.tshirt_size || 'none'}
                onValueChange={handleTshirtChange}
                disabled={savingTshirt}
              >
                <SelectTrigger className="w-32" data-testid="portal-tshirt-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not sure yet</SelectItem>
                  <SelectItem value="S">S</SelectItem>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="L">L</SelectItem>
                  <SelectItem value="XL">XL</SelectItem>
                  <SelectItem value="2XL">2XL</SelectItem>
                  <SelectItem value="3XL">3XL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Dietary restrictions */}
        {attendee.dietary_restrictions && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <UtensilsCrossed className="w-4 h-4 text-gray-400" />
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Dietary Restrictions</p>
              </div>
              <p className="text-sm text-gray-700">{attendee.dietary_restrictions}</p>
            </CardContent>
          </Card>
        )}

        {/* Travel & Directions */}
        <Card className="border-0 shadow-sm" data-testid="portal-travel-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Navigation className="w-4 h-4 text-gray-400" />
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Getting There</p>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Plane className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Fly into Minneapolis-St. Paul (MSP)</p>
                  <p className="text-xs text-gray-500 mt-0.5">Nearest major airport — ~90 min drive to camp</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Herzl Camp</p>
                  <p className="text-xs text-gray-500 mt-0.5">7260 Mickey Smith Parkway</p>
                  <p className="text-xs text-gray-500">Webster, WI 54893</p>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=7260+Mickey+Smith+Parkway+Webster+WI+54893"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 mt-1.5 transition-colors"
                    data-testid="portal-directions-link"
                  >
                    Get directions
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Thermometer className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Expected Weather (mid-September)</p>
                  <p className="text-xs text-gray-500 mt-0.5">Forecast: Highs ~68-70°F (20-21°C) · Lows ~48-54°F (9-12°C) · Chance of rain — bring layers</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packing List */}
        <Card className="border-0 shadow-sm" data-testid="portal-packing-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Backpack className="w-4 h-4 text-gray-400" />
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Packing List</p>
            </div>
            <p className="text-xs text-gray-500 mb-4">Northern Wisconsin, Sept 18–20 forecast: days near 70°F, nights around 50°F. Pack in layers.</p>

            {/* Bring-it-all heads-up */}
            <div
              className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4"
              data-testid="portal-bring-everything-notice"
            >
              <p className="text-sm text-gray-800 leading-relaxed">
                <span className="font-semibold">Good news: pillows and sheets are provided</span> — bring
                your own only if you prefer. Everything else is on you: towels, toiletries, medications,
                and warm layers for the cool nights. When in doubt, pack it.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <PackingCategory title="Essentials" items={PACKING_LIST.essentials} />
              <PackingCategory title="Clothing" items={PACKING_LIST.clothing} />
              <PackingCategory title="Outdoor" items={PACKING_LIST.outdoor} />
            </div>

            {/* Camp policy — no outside food or drinks */}
            <div
              className="mt-6 rounded-xl border border-red-200 bg-gradient-to-br from-red-50/60 to-orange-50/40 p-4 sm:p-5"
              data-testid="portal-no-outside-food-notice"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shrink-0">
                  <Ban className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold tracking-widest text-red-700 uppercase mb-1">
                    Camp Policy
                  </p>
                  <p className="text-sm font-display font-semibold text-gray-900 tracking-tight">
                    No outside food or drinks permitted
                  </p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    All meals, snacks, and beverages are provided on-site. Please leave outside food and
                    drinks at home — including coolers, alcohol, and non-prescription supplements. Bring
                    your refillable water bottle to use at the camp's water stations.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hotel Recommendations */}
        {data.hotels && data.hotels.length > 0 && (
          <Card className="border-0 shadow-sm" data-testid="portal-hotels-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-1">
                <Hotel className="w-4 h-4 text-gray-400" />
                <p className="eyebrow text-gray-400">Where to Stay</p>
              </div>
              <p className="text-sm text-gray-500 mb-5">
                Flying in early or want to break up the drive? Here are nearby hotels brothers have used.
              </p>

              {/* Caravan / shuttle pickup callout */}
              <div
                className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50/60 p-4 mb-5"
                data-testid="portal-shuttle-callout"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
                    <Navigation className="w-4 h-4 text-orange-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold tracking-widest text-orange-700 uppercase mb-1">
                      Shuttle / caravan pickup
                    </p>
                    <p className="text-sm text-gray-800 leading-relaxed">
                      Kevin's shuttles, cars, and the caravan to camp depart from the{' '}
                      <span className="font-semibold">DoubleTree at 7800 Normandale Blvd</span> only.{' '}
                      Either DoubleTree works for your overnight stay, but if you book the Park Place location, plan to get yourself over to Normandale in time for departure.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {data.hotels.map((hotel, i) => (
                  <div
                    key={i}
                    className={`rounded-xl border p-4 transition-colors ${
                      hotel.recommended
                        ? 'border-orange-200 bg-gradient-to-br from-orange-50/40 to-amber-50/20'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`hotel-card-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-display text-xl font-semibold text-gray-900 tracking-tight leading-tight">{hotel.name}</h4>
                          {hotel.recommended && (
                            <span className="text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                              Recommended
                            </span>
                          )}
                        </div>
                        {hotel.area && (
                          <p className="text-sm text-gray-600 mt-1 flex items-start gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                            <span>{hotel.area}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    {hotel.distance && (
                      <p className="text-xs text-gray-500 mb-2">{hotel.distance}</p>
                    )}
                    {hotel.description && (
                      <p className="text-sm text-gray-700 leading-relaxed mb-3">{hotel.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {hotel.url && (
                        <a
                          href={hotel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full px-3 py-1.5 transition-colors"
                          data-testid={`hotel-link-${i}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          View on map
                        </a>
                      )}
                      {hotel.phone && (
                        <a
                          href={`tel:${hotel.phone}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full px-3 py-1.5 transition-colors"
                        >
                          <Phone className="w-3 h-3" />
                          {hotel.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Travel Board */}
        <TravelBoard attendeeId={attendee.id} />

        {/* Contact footer */}
        <div className="text-center pt-2 pb-8">
          <p className="text-xs text-gray-400">
            Questions? Contact{' '}
            <a href="mailto:info@rojoretreat.com" className="text-gray-600 hover:text-gray-900 underline underline-offset-2">
              info@rojoretreat.com
            </a>
          </p>
        </div>
      </div>

      {/* Cancel attendance confirmation modal */}
      <Dialog open={showCancelModal} onOpenChange={(open) => { if (!cancelSubmitting) setShowCancelModal(open); }}>
        <DialogContent className="max-w-md" data-testid="cancel-attendance-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <Ban className="w-4 h-4 text-red-600" />
              </div>
              Cancel your spot?
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-600">
              We'll let the team know you can't make it. You can undo this within 24 hours straight from this page if plans change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Anything you'd like to share? <span className="text-gray-400 normal-case font-normal">(optional)</span>
            </label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. work conflict, family obligation, health…"
              rows={3}
              data-testid="cancel-reason-input"
              className="resize-none"
            />

            {/* Settlement choice — only meaningful if they paid something */}
            {payment && Number(payment.amount_paid) > 0 ? (
              <div className="mt-3 space-y-2" data-testid="settlement-choice-group">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  What should we do with your ${Number(payment.amount_paid).toFixed(0)} payment?
                </p>
                <label
                  htmlFor="settle-refund"
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    settlementChoice === 'refund'
                      ? 'border-red-300 bg-red-50/60'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  data-testid="settlement-refund-option"
                >
                  <input
                    id="settle-refund"
                    type="radio"
                    name="settlement"
                    value="refund"
                    checked={settlementChoice === 'refund'}
                    onChange={() => setSettlementChoice('refund')}
                    className="mt-0.5 w-4 h-4 border-gray-300 text-red-600 focus:ring-red-500"
                    data-testid="settlement-refund-radio"
                  />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      Refund my ${Number(payment.amount_paid).toFixed(0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                      The team will reach out via email to settle. Mark your preferred method (Venmo, etc.) in your reply.
                    </p>
                  </div>
                </label>
                <label
                  htmlFor="settle-donate"
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    settlementChoice === 'donate'
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  data-testid="settlement-donate-option"
                >
                  <input
                    id="settle-donate"
                    type="radio"
                    name="settlement"
                    value="donate"
                    checked={settlementChoice === 'donate'}
                    onChange={() => setSettlementChoice('donate')}
                    className="mt-0.5 w-4 h-4 border-gray-300 text-amber-600 focus:ring-amber-500"
                    data-testid="settlement-donate-radio"
                  />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      Donate my ${Number(payment.amount_paid).toFixed(0)} to ROJO Retreat
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                      Skip the refund — your payment supports next year's scholarships and helps brothers who couldn't otherwise come.
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div
                className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500"
                data-testid="settlement-no-payment"
              >
                You haven't made a payment yet — no refund or donation needed.
              </div>
            )}

            <p className="text-xs text-gray-500 leading-relaxed pt-1">
              {settlementChoice === 'donate' && payment && Number(payment.amount_paid) > 0
                ? "Thank you — we'll record your gift right away."
                : (
                  <>
                    {"We'll also email "}
                    <a href="mailto:info@rojoretreat.com" className="underline underline-offset-2">info@rojoretreat.com</a>
                    {" — please reply to that thread to confirm refund method and timing."}
                  </>
                )}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelSubmitting}
              data-testid="cancel-modal-cancel-btn"
            >
              Never mind
            </Button>
            <Button
              onClick={handleSubmitCancel}
              disabled={cancelSubmitting}
              className={
                settlementChoice === 'donate' && payment && Number(payment.amount_paid) > 0
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }
              data-testid="cancel-modal-confirm-btn"
            >
              {cancelSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : payment && Number(payment.amount_paid) > 0 ? (
                settlementChoice === 'donate'
                  ? `Donate $${Number(payment.amount_paid).toFixed(0)} & cancel`
                  : `Request $${Number(payment.amount_paid).toFixed(0)} refund & cancel`
              ) : (
                "Yes, I can't make it"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-gray-400 font-medium">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
}

function PackingCategory({ title, items }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">{title}</p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
            <span className="w-1 h-1 rounded-full bg-gray-300 mt-1.5 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AttendeePortal;
